import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Chip, Link, Tab, Tabs, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import FileTree from "./FileTree";
import RegistryPanel from "./RegistryPanel";
import {
  ACCENT,
  FONT,
  HAIRLINE,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  SURFACE_MUTED,
} from "../../../../theme/tokens";

function CopyButton({ label, value }) {
  const [copied, setCopied] = useState(false);

  if (!value) return null;

  const handleCopy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Button
      size="small"
      variant="outlined"
      onClick={handleCopy}
      startIcon={
        copied ? (
          <DoneIcon sx={{ fontSize: 16 }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        )
      }
      sx={{
        textTransform: "none",
        fontSize: FONT.xs,
        fontWeight: 600,
        color: copied ? ACCENT : INK_SECONDARY,
        borderColor: HAIRLINE,
        "&:hover": { borderColor: ACCENT, color: ACCENT },
      }}
    >
      {copied ? "Copied" : label}
    </Button>
  );
}

CopyButton.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

export default function ArtifactDetail({
  project,
  categoryLabel,
  defaultTab,
  onBack,
  onOpenFile,
}) {
  const [tab, setTab] = useState(defaultTab);

  return (
    <Box>
      {/* Back to catalog */}
      <Button
        onClick={onBack}
        startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
        sx={{
          textTransform: "none",
          fontSize: FONT.sm,
          fontWeight: 600,
          color: INK_SECONDARY,
          ml: -0.75,
          mb: 1.5,
          "&:hover": { color: ACCENT, backgroundColor: "transparent" },
        }}
      >
        Catalog
      </Button>

      {/* Artifact header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
        <Typography sx={{ fontSize: FONT.xl, fontWeight: 600, color: INK }}>
          {project.name}
        </Typography>
        {categoryLabel && (
          <Chip
            label={categoryLabel}
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: FONT.xs,
              color: INK_SECONDARY,
              borderColor: HAIRLINE,
              backgroundColor: SURFACE_MUTED,
            }}
          />
        )}
      </Box>

      <Typography
        sx={{
          fontSize: FONT.md,
          color: project.description ? INK_SECONDARY : INK_MUTED,
          mb: 1.5,
          maxWidth: 760,
        }}
      >
        {project.description || "No description provided for this repository."}
      </Typography>

      {/* Quick actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          mb: 2.5,
        }}
      >
        <CopyButton label="Copy clone URL" value={project.http_url_to_repo} />
        {project.web_url && (
          <Button
            size="small"
            variant="outlined"
            component={Link}
            href={project.web_url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              fontSize: FONT.xs,
              fontWeight: 600,
              color: INK_SECONDARY,
              borderColor: HAIRLINE,
              "&:hover": { borderColor: ACCENT, color: ACCENT },
            }}
          >
            Open in GitLab
          </Button>
        )}
      </Box>

      {/* Content tabs */}
      <Box sx={{ borderBottom: `1px solid ${HAIRLINE}`, mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: FONT.sm,
              color: INK_SECONDARY,
              minHeight: 42,
              gap: 0.75,
            },
            "& .Mui-selected": { color: ACCENT },
            "& .MuiTabs-indicator": { backgroundColor: ACCENT, height: 2 },
          }}
        >
          <Tab
            icon={<ImageOutlinedIcon sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Images"
          />
          <Tab
            icon={<FolderOutlinedIcon sx={{ fontSize: 17 }} />}
            iconPosition="start"
            label="Files"
          />
        </Tabs>
      </Box>

      {tab === 0 ? (
        <RegistryPanel projectId={project.id} showHeading={false} />
      ) : (
        <FileTree
          projectId={project.id}
          projectName={project.name}
          onOpenFile={onOpenFile}
          showHeading={false}
        />
      )}
    </Box>
  );
}

ArtifactDetail.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    http_url_to_repo: PropTypes.string,
    web_url: PropTypes.string,
  }).isRequired,
  categoryLabel: PropTypes.string,
  defaultTab: PropTypes.number,
  onBack: PropTypes.func.isRequired,
  onOpenFile: PropTypes.func.isRequired,
};

ArtifactDetail.defaultProps = {
  defaultTab: 0,
};
